import { createContext, ReactNode, useCallback, useReducer } from 'react';
// hooks
import { useApiRequest } from '../../hooks/useApiRequest';
import reducer, {
  actions,
  type FavouriteCategory,
  type FavouriteSpace,
  type FavouriteVenue,
  initialState,
} from './reducer';

export interface FavouritesActions {
  fetchFavourites: (type: FavouriteCategory) => Promise<void>;
  addFavourite: (id: number, type: FavouriteCategory) => Promise<void>;
  removeFavourite: (id: number, type: FavouriteCategory) => Promise<void>;
}

export interface FavouritesContextValue {
  venues: FavouriteVenue[];
  spaces: FavouriteSpace[];
  actions: FavouritesActions;
}

export const FavouritesContext = createContext<FavouritesContextValue>({
  ...initialState,
  actions: {
    fetchFavourites: async () => {},
    addFavourite: async () => {},
    removeFavourite: async () => {},
  },
});

const FavouritesProvider = ({ children }: { children?: ReactNode }) => {
  const [{ venues, spaces }, dispatch] = useReducer(reducer, initialState);

  const apiRequest = useApiRequest();

  const fetchFavourites = useCallback(
    async (type: FavouriteCategory) => {
      dispatch({ type: actions.FETCH_FAVOURITES });
      const { data } = await apiRequest.get(`/v1/users/favourites/${type}`);

      dispatch({
        type: actions.FETCH_FAVOURITES_SUCCESS,
        payload: { favourites: data, type },
      });
    },
    [apiRequest]
  );

  const addFavourite = useCallback(
    async (id: number, type: FavouriteCategory) => {
      const { data } = await apiRequest.post(`/v1/users/favourites/${type}`, {
        id,
      });

      dispatch({
        type: actions.ADD_FAVOURITE,
        payload: { favourite: data, type },
      });
    },
    [apiRequest]
  );

  const removeFavourite = useCallback(
    async (id: number, type: FavouriteCategory) => {
      const { data } = await apiRequest.delete(
        `/v1/users/favourites/${type}/${id}`
      );

      dispatch({
        type: actions.REMOVE_FAVOURITE,
        payload: { favourite: data, type },
      });
    },
    [apiRequest]
  );

  const actionMethods = {
    fetchFavourites,
    addFavourite,
    removeFavourite,
  };

  return (
    <FavouritesContext.Provider
      value={{
        venues,
        spaces,
        actions: actionMethods,
      }}
    >
      {children}
    </FavouritesContext.Provider>
  );
};

export default FavouritesProvider;
